import { type V3ChatEntry } from '@apps/web/features/phoebe_chat_v3/model/transcript';

/**
 * Replaces updates to one card and removes exact repeats within a user request.
 * Separate tool calls stay visible as separate cards.
 */
export function collapseDataCards(
    entries: ReadonlyArray<V3ChatEntry>,
): ReadonlyArray<V3ChatEntry> {
    const latestIndexByKey = new Map<string, number>();
    const latestOutreachIndexById = new Map<string, number>();
    const supersededIndexes = new Set<number>();

    entries.forEach((entry, index) => {
        if (entry.kind === 'message' && entry.role === 'user') {
            latestIndexByKey.clear();
            return;
        }
        if (entry.kind !== 'tool') {
            return;
        }

        if (entry.outreachPresentation !== null) {
            const outreachId = entry.outreachPresentation.outreachId;
            const previousIndex = latestOutreachIndexById.get(outreachId);
            if (previousIndex !== undefined) {
                supersededIndexes.add(previousIndex);
            }
            latestOutreachIndexById.set(outreachId, index);
        }

        if (entry.dataPresentation === null) {
            return;
        }

        const keys = [
            JSON.stringify([
                'saved-result',
                entry.runId,
                entry.dataPresentation.filePath,
            ]),
            JSON.stringify([
                'card',
                entry.runId,
                entry.callId ?? entry.toolCallEventId ?? entry.id,
            ]),
        ];
        for (const key of keys) {
            const previousIndex = latestIndexByKey.get(key);
            if (previousIndex !== undefined) {
                supersededIndexes.add(previousIndex);
            }
            latestIndexByKey.set(key, index);
        }
    });

    return entries.filter((_entry, index) => !supersededIndexes.has(index));
}

/**
 * Keeps one outreach chip per outreach, at the point it was first created.
 * A retried `create_outreach` returns the outreach the first attempt made
 * under a new call id, which would otherwise render a second chip.
 */
export function collapseOutreachLinks(
    entries: ReadonlyArray<V3ChatEntry>,
): ReadonlyArray<V3ChatEntry> {
    const seenOutreachIds = new Set<string>();
    return entries.filter((entry) => {
        if (entry.kind !== 'tool' || entry.outreachLink === null) {
            return true;
        }
        const { outreachId } = entry.outreachLink;
        if (seenOutreachIds.has(outreachId)) {
            return false;
        }
        seenOutreachIds.add(outreachId);
        return true;
    });
}
